// TEAM UTILIZATION REPORT
// Team productivity and utilization metrics with charts.
// Phase 7: Reports & Analytics

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Table, Badge, ProgressBar} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  getTeamUtilizationReport,
  TeamUtilizationSummary,
  EmployeeUtilization,
  DailyTrend,
} from '../../../../api/ReportsAPI'
import ExportButton from './ExportButton'

interface Props {
  managerId: string
  startDate: string
  endDate: string
}

const TeamUtilization: React.FC<Props> = ({managerId, startDate, endDate}) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<TeamUtilizationSummary | null>(null)
  const [byEmployee, setByEmployee] = useState<EmployeeUtilization[]>([])
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([])

  useEffect(() => {
    if (managerId && startDate && endDate) {
      loadReport()
    }
  }, [managerId, startDate, endDate])

  const loadReport = async () => {
    try {
      setLoading(true)
      const response = await getTeamUtilizationReport({
        managerId,
        startDate,
        endDate,
        includeLeave: true,
      })

      if (response.data?.statusCode === 200) {
        const data = response.data.data
        setSummary(data.summary)
        setByEmployee(data.byEmployee)
        setDailyTrend(data.dailyTrend)
      }
    } catch (error: any) {
      console.error('Error loading team utilization report:', error)
      toast.error(error.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  const getUtilizationColor = (rate: number) => {
    if (rate >= 90) return 'success'
    if (rate >= 75) return 'info'
    if (rate >= 60) return 'warning'
    return 'danger'
  }

  if (loading) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border text-primary' />
        <p className='mt-2 text-muted'>Loading report...</p>
      </div>
    )
  }

  if (!summary) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-chart-bar fa-3x text-muted mb-3'></i>
        <p className='text-muted'>No data available for selected period</p>
      </div>
    )
  }

  return (
    <>
      {/* Export Button */}
      <Row className='mb-3'>
        <Col className='d-flex justify-content-end'>
          <ExportButton
            reportType='team-utilization'
            filters={{
              managerId,
              startDate,
              endDate,
              includeLeave: true,
            }}
          />
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card className='border-primary'>
            <Card.Body className='text-center'>
              <h4 className='text-primary mb-0'>{summary.totalBillableHours}h</h4>
              <small className='text-muted'>Total Billable Hours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-info'>
            <Card.Body className='text-center'>
              <h4 className='text-info mb-0'>{summary.totalActualHours}h</h4>
              <small className='text-muted'>Total Actual Hours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={`border-${getUtilizationColor(summary.utilizationRate)}`}>
            <Card.Body className='text-center'>
              <h4 className={`text-${getUtilizationColor(summary.utilizationRate)} mb-0`}>
                {summary.utilizationRate}%
              </h4>
              <small className='text-muted'>Utilization Rate</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-warning'>
            <Card.Body className='text-center'>
              <h4 className='text-warning mb-0'>{summary.overtimeHours}h</h4>
              <small className='text-muted'>Overtime Hours</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Additional Metrics */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card>
            <Card.Body className='text-center'>
              <h5 className='mb-0'>{summary.teamSize}</h5>
              <small className='text-muted'>Team Size</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className='text-center'>
              <h5 className='mb-0'>{summary.workingDays}</h5>
              <small className='text-muted'>Working Days</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className='text-center'>
              <h5 className='mb-0'>{summary.leaveHours}h</h5>
              <small className='text-muted'>Leave Hours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className='text-center'>
              <h5 className='mb-0'>{summary.holidayHours}h</h5>
              <small className='text-muted'>Holiday Hours</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Employee Breakdown Table */}
      <Row className='mb-4'>
        <Col>
          <Card>
            <Card.Header>
              <h5 className='mb-0'>Employee Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Billable Hours</th>
                    <th>Actual Hours</th>
                    <th>Utilization</th>
                    <th>Overtime</th>
                    <th>Entries</th>
                    <th>Approved</th>
                    <th>Rejected</th>
                  </tr>
                </thead>
                <tbody>
                  {byEmployee.map((emp) => (
                    <tr key={emp.userId}>
                      <td>
                        <strong>{emp.name}</strong>
                        <br />
                        <small className='text-muted'>{emp.employeeCode}</small>
                      </td>
                      <td>{emp.billableHours}h</td>
                      <td>{emp.actualHours}h</td>
                      <td>
                        <Badge bg={getUtilizationColor(emp.utilizationRate)}>
                          {emp.utilizationRate}%
                        </Badge>
                        <ProgressBar
                          now={emp.utilizationRate}
                          variant={getUtilizationColor(emp.utilizationRate)}
                          className='mt-1'
                          style={{height: '5px'}}
                        />
                      </td>
                      <td>
                        {emp.overtimeHours > 0 ? (
                          <Badge bg='warning' text='dark'>
                            {emp.overtimeHours}h
                          </Badge>
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                      <td>{emp.entriesCount}</td>
                      <td>
                        <Badge bg='success'>{emp.approvedCount}</Badge>
                      </td>
                      <td>
                        {emp.rejectedCount > 0 ? (
                          <Badge bg='danger'>{emp.rejectedCount}</Badge>
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className='table-secondary'>
                    <th>Total</th>
                    <th>{summary.totalBillableHours}h</th>
                    <th>{summary.totalActualHours}h</th>
                    <th>
                      <Badge bg={getUtilizationColor(summary.utilizationRate)}>
                        {summary.utilizationRate}%
                      </Badge>
                    </th>
                    <th>{summary.overtimeHours}h</th>
                    <th colSpan={3}>-</th>
                  </tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Daily Trend Chart (Simple Bar Chart) */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className='mb-0'>Daily Hours Trend</h5>
            </Card.Header>
            <Card.Body>
              {dailyTrend.length > 0 ? (
                <div style={{overflowX: 'auto'}}>
                  <div style={{minWidth: '800px', height: '300px', position: 'relative'}}>
                    {dailyTrend.map((day, index) => {
                      const maxHours = Math.max(
                        ...dailyTrend.map((d) => d.actualHours),
                        100
                      )
                      const billableHeight = (day.billableHours / maxHours) * 250
                      const actualHeight = (day.actualHours / maxHours) * 250

                      return (
                        <div
                          key={index}
                          style={{
                            display: 'inline-block',
                            width: '40px',
                            marginRight: '10px',
                            verticalAlign: 'bottom',
                          }}
                        >
                          <div style={{height: '250px', display: 'flex', alignItems: 'flex-end', gap: '5px'}}>
                            <div
                              title={`Billable: ${day.billableHours}h`}
                              style={{
                                width: '15px',
                                height: `${billableHeight}px`,
                                backgroundColor: '#0d6efd',
                                borderRadius: '3px 3px 0 0',
                              }}
                            />
                            <div
                              title={`Actual: ${day.actualHours}h`}
                              style={{
                                width: '15px',
                                height: `${actualHeight}px`,
                                backgroundColor: '#0dcaf0',
                                borderRadius: '3px 3px 0 0',
                              }}
                            />
                          </div>
                          <div
                            style={{
                              fontSize: '10px',
                              textAlign: 'center',
                              marginTop: '5px',
                              transform: 'rotate(-45deg)',
                              transformOrigin: 'left top',
                            }}
                          >
                            {day.date.substring(5)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className='mt-3 d-flex justify-content-center gap-4'>
                    <div>
                      <span
                        className='d-inline-block me-2'
                        style={{
                          width: '20px',
                          height: '10px',
                          backgroundColor: '#0d6efd',
                        }}
                      ></span>
                      <small>Billable Hours</small>
                    </div>
                    <div>
                      <span
                        className='d-inline-block me-2'
                        style={{
                          width: '20px',
                          height: '10px',
                          backgroundColor: '#0dcaf0',
                        }}
                      ></span>
                      <small>Actual Hours</small>
                    </div>
                  </div>
                </div>
              ) : (
                <p className='text-center text-muted'>No daily data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default TeamUtilization

